/* eslint-disable react/prop-types, no-underscore-dangle */

import { useState } from "react";
import Button from "../../shared/v2/Button";
import Modal from "../../shared/v2/Modal";
import TextButton from "../../shared/v2/TextButton";
import AgentsFilter from "./shared/Filters/AgentsFilter";
import SourceFilter from "./shared/Filters/SourceFilter";
import DateFilter from "./shared/Filters/DateFilter";
import FilterInlineOptions from "./FilterInlineOptions";
import SearchFilter from "./shared/Filters/SearchFilter";

const FiltersModal = ({
  initialFilters,
  isModalShown,
  onModalClose,
  initialTypes,
  initialAgents,
  initialPublishedStates,
  initialStatuses,
  initialSources,
  mutualAcceptanceDateOptions,
  dateRangeOptions,
  statusDateOptions,
  onClearAll,
  onFilter,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const setAgents = (agents) => {
    setFilters({ ...filters, agent: agents });
  };

  const setType = (types) => {
    setFilters({
      ...filters,
      type: types.filter((f) => f.isActive).map((f) => f.id),
    });
  };

  const setPublishedState = (publishedStates) => {
    setFilters({
      ...filters,
      publishedState: publishedStates.filter((f) => f.isActive).map((f) => f.id),
    });
  };

  const setStatus = (statuses) => {
    setFilters({
      ...filters,
      status: statuses.filter((f) => f.isActive).map((f) => f.id),
    });
  };

  const setSource = (sources) => {
    setFilters({ ...filters, source: sources });
  };

  const setMutualAcceptanceRange = (rangeId) => {
    setFilters({ ...filters, mutualAcceptanceDateRange: rangeId ? [rangeId] : [] });
  };

  const setMutualAcceptanceDate = (dates) => {
    setFilters({ ...filters, mutualAcceptanceDateDates: dates || [] });
  };

  const setCloseRange = (rangeId) => {
    setFilters({ ...filters, closeDateRange: rangeId ? [rangeId] : [] });
  };

  const setCloseDate = (dates) => {
    setFilters({ ...filters, closeDateDates: dates || [] });
  };

  const setStatusRange = (rangeId) => {
    setFilters({ ...filters, statusChangedRange: rangeId ? [rangeId] : [] });
  };

  const setStatusDate = (dates) => {
    setFilters({ ...filters, statusChangedDates: dates || [] });
  };

  const _filters = { ...initialFilters };

  Object.keys(_filters).forEach((k) => {
    _filters[k] = Array.isArray(filters[k]) ? new Set(filters[k].map((x) => x)) : filters[k];
  });
  const usedStatuses = initialStatuses.map((item) => ({ ...item, isActive: _filters.status.has(item.id) }));
  const usedPublishedStates = initialPublishedStates.map((item) => ({
    ...item,
    isActive: _filters.publishedState.has(item.id),
  }));
  const usedTypes = initialTypes.map((item) => ({ ...item, isActive: _filters.type.has(item.id) }));
  const initialCloseDateRange = Array.from(_filters.closeDateRange)[0];
  const initialCloseDateDates = Array.from(_filters.closeDateDates);
  const initialMutualAcceptanceDateRange = Array.from(_filters.mutualAcceptanceDateRange)[0];
  const initialMutualAcceptanceDateDates = Array.from(_filters.mutualAcceptanceDateDates);
  const initialStatusChangedRange = Array.from(_filters.statusChangedRange)[0];
  const initialStatusChangedDates = Array.from(_filters.statusChangedDates);

  return (
    <Modal
      show={isModalShown}
      onHide={onModalClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[600px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
      data-cy="filter-modal"
    >
      <Modal.Header title="Filter" closeButton />
      <Modal.Body className="tw-flex-1 tw-overflow-y-auto tw-gap-[24px] tw-flex tw-flex-col">
        <div data-cy="agents-filter-section">
          <AgentsFilter initialAgents={initialAgents} onAgentsChange={setAgents} agentIds={filters.agent} />
        </div>
        <FilterInlineOptions
          initialStatuses={usedStatuses}
          onStatusesChange={setStatus}
          initialPublishedStates={usedPublishedStates}
          onPublishedStatesChange={setPublishedState}
          initialTypes={usedTypes}
          onTypesChange={setType}
        />
        <div>
          <SourceFilter
            initialSources={initialSources}
            onSourceChange={setSource}
            sourceIds={filters.source}
          />
        </div>
        <div>
          <DateFilter
            initiallySelectedOption={initialMutualAcceptanceDateRange}
            initiallySelectedDateRange={initialMutualAcceptanceDateDates}
            dateRangeOptions={mutualAcceptanceDateOptions}
            onChangeRange={setMutualAcceptanceRange}
            onChangeDates={setMutualAcceptanceDate}
            label="Mutual Acceptance Date Range"
            placeholder="Select a mutual acceptance date range"
          />
        </div>
        <div>
          <DateFilter
            initiallySelectedOption={initialCloseDateRange}
            initiallySelectedDateRange={initialCloseDateDates}
            dateRangeOptions={dateRangeOptions}
            onChangeRange={setCloseRange}
            onChangeDates={setCloseDate}
            label="Close Date Range"
            placeholder="Select a close date range"
          />
        </div>
        <div>
          <DateFilter
            initiallySelectedOption={initialStatusChangedRange}
            initiallySelectedDateRange={initialStatusChangedDates}
            dateRangeOptions={statusDateOptions}
            onChangeRange={setStatusRange}
            onChangeDates={setStatusDate}
            label="Status Change Date Range"
            placeholder="Select a date range for status change"
          />
        </div>
        <div>
          <SearchFilter value={filters.search} />
        </div>
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between">
        <Button schema="tertiary" size="medium" onClick={() => onModalClose()}>
          Cancel
        </Button>
        <div>
          <TextButton className="tw-mr-16px" size="medium" onClick={() => onClearAll()}>
            Clear All
          </TextButton>
          <Button size="medium" onClick={() => onFilter(filters)}>
            Apply
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default FiltersModal;
